import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-kampong-kleang.jpg";

const itinerary = [
  {
    day: "Day 1",
    title: "Angkor Wat Tour",
    body: "Visit the highlights of Angkor Archaeological Park, including sunrise at Angkor Wat, the ancient city of Angkor Thom, the smiling faces of Bayon Temple, and the jungle-covered Ta Prohm. A perfect introduction to Angkor's history and beauty.",
  },
  {
    day: "Day 2",
    title: "Phnom Kulen Waterfall",
    body: "Travel to Phnom Kulen National Park, the sacred mountain of Cambodia. Discover the River of a Thousand Lingas, a reclining Buddha, and enjoy swimming at the beautiful Kulen waterfall surrounded by nature.",
  },
  {
    day: "Day 3",
    title: "Banteay Srei & Grand Circuit",
    body: "Explore the intricate pink sandstone carvings of Banteay Srei, then continue to the Grand Circuit temples, including Pre Rup, Neak Pean, Ta Som, and East Mebon offering a quieter and more relaxed temple experience.",
  },
  {
    day: "Day 4",
    title: "Kampong Khleang Floating Village",
    body: "Visit Kampong Khleang, one of the largest and most authentic villages on Tonle Sap Lake. Take a boat trip to see stilt houses, local life, and the unique fishing culture of Cambodia.",
  },
];

const sunriseSchedule = [
  {
    time: "4:15 AM – 6:00 AM",
    title: "Early Morning",
    items: [
      "Hotel pickup in a comfortable, air-conditioned vehicle",
      "Arrival at Angkor Archaeological Park",
      "Enter Angkor Wat via the quieter eastern entrance",
      "Walk through ancient corridors using a flashlight",
    ],
  },
  {
    time: "6:00 AM – 8:30 AM",
    title: "Sunrise Experience",
    items: [
      "Watch the sunrise over Angkor Wat from the reflecting (library) pool",
      "Receive basic photography guidance from your guide",
      "Enjoy a two-hour, in-depth exploration of the temple",
      "Discover the stories behind the intricate bas-relief carvings",
    ],
  },
  {
    time: "9:00 AM – 12:00 PM",
    title: "Temple Exploration",
    items: [
      "Optional breakfast break (not included)",
      "Explore the jungle-covered temple of Ta Prohm",
      "Visit the ancient city of Angkor Thom",
      "Discover Bayon Temple, famous for its 200+ stone faces",
      "Walk through the South Gate lined with gods and demons",
    ],
  },
  {
    time: "12:30 PM – 1:30 PM",
    title: "Return Journey",
    items: [
      "Relax on a comfortable drive back to your hotel",
      "Time to rest and reflect on an unforgettable morning at Angkor",
    ],
  },
];

const TourSiemReap4Day = () => {
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Siem Reap 4 day private tour — Angkor Wat, Phnom Kulen waterfall and Tonlé Sap floating village with French-speaking guide"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 hidden sm:inline">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 4-Day Tours in Siem Reap</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance lg:text-2xl">
              4 Days Siem Reap&nbsp;Angkor Wat, Kulen Waterfall &amp; Floating Village.
            </h1>
            <p className="mt-10 max-w-xl text-background/70 leading-relaxed text-xl font-light">
              Four unhurried days through the heart of Cambodia sunrise at Angkor Wat, the sacred mountain of Phnom Kulen, the rose-pink carvings of Banteay Srei, and the stilt-house life of Kampong Khleang on Tonlé Sap.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 4 Days</span>
              <span>Group · 2–15</span>
              <span>From $1,940</span>
            </div>
          </div>
        </div>
      </header>

      {/* ITINERARY */}
      <section className="py-24 md:py-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 4-Day Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Four days, four worlds of Siem Reap.
              </h2>
              <ol className="relative border-l border-border pl-8 md:pl-10 space-y-12">
                {itinerary.map((d) => (
                  <li key={d.day} className="relative">
                    <span className="absolute -left-[41px] md:-left-[49px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                    <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-2">{d.day}</p>
                    <h3 className="font-display text-2xl md:text-3xl mb-3">{d.title}</h3>
                    <p className="text-muted-foreground font-light leading-relaxed text-lg">{d.body}</p>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={415} tourTitle="Siem Reap Tour, Angkor Wat Tour, 4 Day Itinerary" pricingTiers={[{ maxPax: 2, price: 415 }, { maxPax: 6, price: 465 }, { maxPax: 10, price: 512 }]} />
          </div>
        </div>
      </section>

      {/* SUNRISE SCHEDULE */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Day 1 In Detail</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
            Complete sunrise tour schedule.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12">
            {sunriseSchedule.map((s) => (
              <div key={s.title}>
                <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-2">{s.time}</p>
                <h3 className="font-display text-2xl mb-5">{s.title}</h3>
                <ul className="space-y-3 font-light text-muted-foreground">
                  {s.items.map((it) => (
                    <li key={it}>• {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTION / OPTIONS */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Tour Collection</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-10 text-balance">
            Discover our Angkor Wat tours.
          </h2>
          <p className="text-muted-foreground font-light text-lg leading-relaxed mb-16 max-w-3xl">
            After planning thousands of trips to Cambodia, one thing is clear: not all Angkor tours are created equal. Some rush you through in just a few hours, while others charge premium prices for a basic experience. Here's what truly makes the difference.
          </p>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-3">Small Group Adventures (2–15 people)</h3>
              <p className="text-muted-foreground font-light leading-relaxed">Perfect for travellers who want a more personal experience without the cost of a private tour.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Private Temple Experiences</h3>
              <p className="text-muted-foreground font-light leading-relaxed">Full flexibility explore at your own pace, start at sunrise, take breaks when you like.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Multi-Language Options</h3>
              <p className="text-muted-foreground font-light leading-relaxed">Tours in German, Spanish, French, and Italian with native-speaking guides.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Regional Departures</h3>
              <p className="text-muted-foreground font-light leading-relaxed">Arriving from Singapore or Ho Chi Minh City? We coordinate your tour timing smoothly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDES / EXCLUDES */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl grid md:grid-cols-2 gap-x-12 gap-y-12">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
            <ul className="space-y-4 font-light text-lg">
              <li className="border-b border-border pb-4">Air-conditioned transportation with hotel pickup &amp; drop-off</li>
              <li className="border-b border-border pb-4"></li>
              <li className="border-b border-border pb-4">Complimentary bottled water</li>
              <li className="border-b border-border pb-4">Refreshing cold towels</li>
              <li className="pb-4">Photography guidance for the best angles and lighting</li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Not Included</p>
            <ul className="space-y-4 font-light text-lg">
              <li className="border-b border-border pb-4">Meals</li>
              <li className="pb-4">Temple entrance fee: US$37 (valid for all temples in one day) purchase via the official Angkor Pass website</li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Essential Items</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">What to bring, what to leave behind.</h2>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-5">Required</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Flashlight or phone light</li>
                <li>Temple pass (US$37)</li>
                <li>Modest clothing shoulders &amp; knees covered</li>
                <li>Comfortable walking shoes</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Recommended</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Camera with extra batteries</li>
                <li>Small backpack</li>
                <li>Cash for breakfast</li>
                <li>Sun protection hat, sunscreen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Leave at Hotel</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Passport (bring a copy)</li>
                <li>Airline tickets</li>
                <li>Excess cash</li>
                <li>Valuable jewellery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>Sunrise tours are the most popular and tend to sell out quickly, especially during the dry season (November to March). We recommend booking at least 48 hours in advance during peak season, even earlier.</p>
            <p>Pickup time: 4:15 AM – 4:35 AM (exact time confirmed one day before). Tour ends 12:30 PM – 1:30 PM.</p>
            <p>Breakfast: if your hotel includes breakfast, request a takeaway box, or stop at a local restaurant.</p>
            <p>Dress code: casual but respectful covered shoulders and knees required inside temples. Comfortable walking shoes are strongly recommended.</p>
            <p>For safety, leave valuables at your hotel and bring only what you need. This tour is not wheelchair accessible. Children under 8 are not permitted. Maximum group size: 2–15 travellers in an air-conditioned vehicle.</p>
            <p>
              <span className="text-foreground">Weather.</span> Hot and dry weather is expected throughout this tour. There is always the possibility of a shower or two, but it is likely to be dry most of the time. Midday temperatures are likely to be very hot, but most of the woodland sites we visit have low humidity.
            </p>
            <p>
              <span className="text-foreground">Food.</span> Khmer cuisine is similar to most Southeast Asian cooking, but rather less spicy. Good bread is available in many areas thanks to the French influence, and there is a surprisingly wide range of local brews to try.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Reserve Your Sojourn</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-10 text-balance">
            Four days. One unforgettable Cambodia.
          </h2>
          <Link
            to="/#contact"
            className="inline-block bg-foreground text-background px-10 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-primary transition-colors"
          >
            Book Now →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default TourSiemReap4Day;
